import { useState, FormEvent } from "react";
import { useTheme } from "../../../contexts/ThemeContext";

const FORM_URL = "https://readdy.ai/api/form/d744q0mcsbv12mkucvg0";
const industries = ["Home Services (HVAC, Plumbing, Electrical, etc.)", "Gym / Fitness Studio", "Wellness / Spa / Massage", "Chiropractic / Physical Therapy", "Other Service Business"];

export default function ContactSection() {
  const { isDark } = useTheme();
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((value, key) => body.append(key, value.toString()));
    setStatus("loading");
    try {
      const res = await fetch(FORM_URL, { method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body: body.toString() });
      if (res.ok) { setStatus("success"); form.reset(); setCharCount(0); }
      else setStatus("error");
    } catch { setStatus("error"); }
  };

  const sectionBg = isDark ? "bg-black" : "bg-white";
  const titleColor = isDark ? "text-white" : "text-[#1d1d1f]";
  const subColor = isDark ? "text-white/45" : "text-[#6e6e73]";
  const cardBg = isDark ? "bg-[#0c0c14] border-white/[0.07]" : "bg-[#F5F5F7] border-black/[0.08]";
  const labelColor = isDark ? "text-white/45" : "text-[#6e6e73]";
  const inputBg = isDark ? "bg-black/60 border-white/[0.08] text-white placeholder:text-white/20" : "bg-white border-black/10 text-[#1d1d1f] placeholder:text-black/25";
  const inputFocusO = isDark ? "focus:border-[#F97316]/40" : "focus:border-[#F97316]/60";
  const inputFocusB = isDark ? "focus:border-[#38BDF8]/40" : "focus:border-[#38BDF8]/60";
  const dividerColor = isDark ? "border-white/[0.07]" : "border-black/[0.07]";
  const stepNumColor = isDark ? "text-white/50 bg-white/5" : "text-[#6e6e73] bg-black/5";
  const stepTextColor = isDark ? "text-white/45" : "text-[#6e6e73]";
  const successIconBg = isDark ? "bg-[#22C55E]/10" : "bg-[#22C55E]/10";
  const successBtnBorder = isDark ? "border-white/15 text-white/60 hover:text-white hover:border-white/30" : "border-black/15 text-black/50 hover:text-black hover:border-black/30";
  const sectionLabel = isDark ? "text-white/25" : "text-black/25";
  const contactInfoBg = isDark ? "bg-white/5" : "bg-black/5";
  const optionBg = isDark ? "bg-[#0c0c14]" : "bg-white";
  const selectBg = isDark ? `${inputBg}` : `${inputBg}`;

  return (
    <section id="contact" className={`${sectionBg} py-24 md:py-36 px-6 md:px-10 transition-colors duration-300`}>
      <div className="max-w-[1280px] mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left */}
          <div>
            <p className="text-[#F97316] text-[11px] font-semibold tracking-[0.2em] uppercase mb-5">Get Started</p>
            <h2 className={`text-[42px] md:text-[58px] font-black ${titleColor} tracking-[-0.03em] leading-tight mb-6`}>
              Deploy your AI<br />workforce today.
            </h2>
            <p className={`${subColor} text-[16px] leading-relaxed mb-12 max-w-md`}>
              Tell us about your business. We&apos;ll assess your lead flow, identify your gaps, and show you exactly how an AI workforce drives measurable growth.
            </p>

            <div className="flex flex-col gap-5 mb-12">
              {[
                { icon: "ri-mail-line", color: "#38BDF8", label: "Email", value: "hello@aitalentlaunch.com" },
                { icon: "ri-phone-line", color: "#22C55E", label: "Phone", value: "(800) 123-4567" },
                { icon: "ri-time-line", color: "#F97316", label: "Response", value: "Within 1 business day" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className={`w-10 h-10 flex items-center justify-center ${contactInfoBg} rounded-xl shrink-0`}>
                    <i className={`${item.icon} text-[16px]`} style={{ color: item.color }}></i>
                  </div>
                  <div>
                    <p className={`${sectionLabel} text-[11px] font-medium uppercase tracking-wider`}>{item.label}</p>
                    <p className={`${titleColor} text-[14px] font-medium`}>{item.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className={`border-t ${dividerColor} pt-10`}>
              <p className={`${sectionLabel} text-[11px] uppercase tracking-widest mb-5`}>What happens next</p>
              <div className="flex flex-col gap-4">
                {[
                  { text: "We review your submission and business profile", color: "#38BDF8" },
                  { text: "You receive a personalized deployment assessment", color: "#F97316" },
                  { text: "We schedule a strategy call at your convenience", color: "#22C55E" },
                  { text: "Your AI workforce goes live within days", color: "#F97316" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3.5">
                    <span className={`w-[22px] h-[22px] flex items-center justify-center shrink-0 text-[11px] font-bold rounded-full ${stepNumColor}`} style={{ color: item.color }}>{i + 1}</span>
                    <span className={`${stepTextColor} text-[14px] leading-relaxed`}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div className={`border rounded-[24px] p-8 md:p-10 ${cardBg}`}>
            {status === "success" ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className={`w-14 h-14 flex items-center justify-center ${successIconBg} rounded-full mb-6`}>
                  <i className="ri-check-double-line text-[#22C55E] text-2xl"></i>
                </div>
                <h3 className={`${titleColor} font-black text-[24px] tracking-tight mb-3`}>Message Received</h3>
                <p className={`${subColor} text-[14px] max-w-xs leading-relaxed`}>Our team will review your submission and reach back within 1 business day.</p>
                <button onClick={() => setStatus("idle")} className={`mt-8 border text-[13px] font-medium px-6 py-2.5 rounded-full transition-all cursor-pointer whitespace-nowrap ${successBtnBorder}`}>
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="flex flex-col gap-5">
                <div>
                  <h3 className={`${titleColor} font-black text-[20px] tracking-tight mb-1`}>Free Deployment Assessment</h3>
                  <p className={`${subColor} text-[13px]`}>No commitment. No credit card required.</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[{ name: "first_name", label: "First Name *", ph: "John", focus: inputFocusO }, { name: "last_name", label: "Last Name *", ph: "Smith", focus: inputFocusO }].map((f) => (
                    <div key={f.name}>
                      <label className={`${labelColor} text-[12px] font-medium mb-1.5 block`}>{f.label}</label>
                      <input type="text" name={f.name} required placeholder={f.ph} className={`w-full border rounded-[14px] px-4 py-3 text-[14px] focus:outline-none transition-colors ${inputBg} ${f.focus}`} />
                    </div>
                  ))}
                </div>

                <div>
                  <label className={`${labelColor} text-[12px] font-medium mb-1.5 block`}>Email *</label>
                  <input type="email" name="email" required placeholder="john@yourbusiness.com" className={`w-full border rounded-[14px] px-4 py-3 text-[14px] focus:outline-none transition-colors ${inputBg} ${inputFocusB}`} />
                </div>

                <div>
                  <label className={`${labelColor} text-[12px] font-medium mb-1.5 block`}>Phone</label>
                  <input type="tel" name="phone" placeholder="(555) 000-0000" className={`w-full border rounded-[14px] px-4 py-3 text-[14px] focus:outline-none transition-colors ${inputBg} ${inputFocusB}`} />
                </div>

                <div>
                  <label className={`${labelColor} text-[12px] font-medium mb-1.5 block`}>Business Name *</label>
                  <input type="text" name="business_name" required placeholder="Your Company Name" className={`w-full border rounded-[14px] px-4 py-3 text-[14px] focus:outline-none transition-colors ${inputBg} ${inputFocusO}`} />
                </div>

                <div>
                  <label className={`${labelColor} text-[12px] font-medium mb-1.5 block`}>Industry *</label>
                  <select name="industry" required className={`w-full border rounded-[14px] px-4 py-3 text-[14px] focus:outline-none transition-colors cursor-pointer ${selectBg} ${inputFocusB}`}>
                    <option value="" className={optionBg}>Select your industry</option>
                    {industries.map((ind) => <option key={ind} value={ind} className={optionBg}>{ind}</option>)}
                  </select>
                </div>

                <div>
                  <label className={`${labelColor} text-[12px] font-medium mb-1.5 block`}>
                    Biggest challenge <span className={`ml-1 ${sectionLabel}`}>({charCount}/500)</span>
                  </label>
                  <textarea name="message" rows={4} maxLength={500} placeholder="e.g. We miss calls when our team is on-site..." onChange={(e) => setCharCount(e.target.value.length)} className={`w-full border rounded-[14px] px-4 py-3 text-[14px] focus:outline-none transition-colors resize-none ${inputBg} ${inputFocusO}`} />
                </div>

                {status === "error" && <p className="text-[#F97316] text-[13px]">Something went wrong. Please try again.</p>}

                <button type="submit" disabled={status === "loading"} className="w-full bg-[#F97316] text-white font-bold py-3.5 rounded-full hover:bg-[#FB923C] disabled:opacity-50 transition-all duration-200 cursor-pointer whitespace-nowrap mt-1 text-[14px]">
                  {status === "loading" ? "Submitting..." : "Get My Free Assessment \u2192"}
                </button>
                <p className={`${sectionLabel} text-[11px] text-center`}>Confidential &bull; No credit card &bull; Free consultation</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
